import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from "@angular/core";
import { UntypedFormBuilder } from "@angular/forms";
import { ModalController, Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { ColumnMode, SelectionType } from "@swimlane/ngx-datatable";
import { Subscription } from "rxjs";
import { filter, map } from "rxjs/operators";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import {
  Environment,
  EnvironmentTree,
} from "src/components/shared-components/class/general-classes/environment";
import { Gateway } from "src/components/shared-components/class/general-classes/gateway";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { EnvironmentCategory } from "src/components/shared-components/enum/enumEnvironment";
import { ApplicationTheme } from "src/components/shared-components/enum/enumMisc";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { iConnToolbarColour } from "src/components/shared-components/interfaces/iConnToolbarColour";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { Building } from "src/components/shared-components/class/general-classes/building";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { Supervision } from "src/model/class/general classes/supervision";
import { User } from "src/model/class/general classes/user";
import { Settings } from "src/model/class/settings";
import { ActionEnvironments } from "src/model/enum/enumActionSheetOperation";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { ActionSheetService } from "src/service/action-sheet.service";
import { AlertService } from "src/service/alert.service";
import { DpBuildingService } from "src/service/building.service";
import { DeviceService } from "src/service/device.service";
import { EnvironmentService } from "src/service/environment.service";
import { EventService } from "src/service/event.service";
import { LoaderService } from "src/service/loader.service";
import { NetworkService } from "src/service/network.service";
import { PermissionService } from "src/service/permission.service";
import { SupervisionService } from "src/service/supervision.service";
import { SystemRouterService } from "src/service/system-router.service";
import { ThemeService } from "src/service/theme.service";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { serverSettings } from "src/state/selectors/server-settings-selector";

@Component({
    selector: "app-environment-item",
    templateUrl: "./environment-item.component.html",
    styleUrls: ["./environment-item.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EnvironmentItemComponent
  implements OnInit, OnDestroy, iConnToolbarColour, iTranslation, iEvent {
  private _logContext: string = "Environment-item.component";
  private _lblClose: string = "";
  private _lblDelete: string = "";
  private _lblManage: string = "";
  private _environmentStoreSubscription: Subscription = null;
  private _networkSubscription: Subscription = null;
  private _translateSubscription: Subscription = null;
  private _listGateway: Array<Gateway> = null;
  private _lblOther: string = "";
  private _lblConfirmDeleteBuildingPart: string = "";
  private _lblDeleteMonitoringPart: string = "";
  private _lblUser: string = "";

  private _currentSupervision: Supervision = null;
  private _selectCurrentUserSub: Subscription = null;
  private _selectserverSettingsSub: Subscription = null;
  private _envHasDevicesAndChildren: boolean = false;
  private _isLoadingActionSheet: boolean = false;

  public _eventSubscriptions: Array<Subscription> = null;
  public environmentList: Environment[] = [];
  public environmentFilteredList: Environment[] = [];
  public connectionType = ConnectionType;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public toolBarColor: string = "primary";
  public showErrorContent: boolean = false;
  public createdIonicSelect: boolean = false;
  public reorderable: boolean = false;
  public panelOpenState: boolean = false;
  public environmentSelected: any;
  public selectedUser: Array<User> = new Array<User>();
  public listUsers: Array<User> = new Array<User>();
  public listUsersSelected: Array<User> = new Array<User>();
  public currentBuilding: Building;
  public selected: any[] = [];
  public ColumnMode = ColumnMode;
  public SelectionType = SelectionType;
  public currentUser: CurrentSessionData = null;
  //#region Translated string
  public noEnviromentFound: string = "*";
  public searchEnv: string = "*";
  public gatewayName: string = "";
  public nRoom: string = "";
  public description: string = "";
  public idEnvironment: number;
  public listBuildings: Array<Building> = new Array();
  public listUserConcat: string = "";
  public generalSettings: Settings = null;
  public showActionMenu: boolean = false;
  public showGrid: boolean = true;
  //#endregion

  @Input() currentEnvironment: EnvironmentTree;
  @Input() idArea: number;
  @Input() idBuilding: number;
  @Input() isDemoMode: boolean;
  @Input() level: number = 0;
  @Input() showChildren: boolean = false;
  @Output() deleteEnv = new EventEmitter<EnvironmentTree>();

  // variable to make ion-item ad a button (for supervisions page)
  @Input() areThereButtons: boolean = false;

  // variable to show or hide access icon (for supervisions page)
  @Input() showIconKey: boolean = true;

  constructor(
    private _fb: UntypedFormBuilder,
    private _environmentService: EnvironmentService,
    private _modalCtrl: ModalController,
    private _networkService: NetworkService,
    private _alertService: AlertService,
    private _translateService: TranslateService,
    private _platform: Platform,
    private _loaderService: LoaderService,
    private _eventService: EventService,
    private _themeService: ThemeService,
    private _routerService: SystemRouterService,
    private _buildingService: DpBuildingService,
    private _permissionService: PermissionService,
    private _actionSheetService: ActionSheetService,
    private _store: Store,
    private _deviceService: DeviceService,
    private _systemRouterService: SystemRouterService
  ) { }

  ngOnInit() {
    this.loadCurrentUserData();
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.subsribeNetworkServiceData();
    this.subscribeEvents();

    this.loadSettingsData();
    this.checkIfEnvironmentHasDevicesAndChildren();

    this.showChildren = this._environmentService.isExpanded(
      this.currentEnvironment.id
    );
  }

  ngOnDestroy(): void {
    this._networkSubscription?.unsubscribe();
    this._translateSubscription?.unsubscribe();

    this.unsubscribeStoreSelect();
    this.unsubscribeEvents();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        this._modalCtrl.dismiss();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Generate single tab icon by key segment value
   *
   * @param keySegment Key segment to get css icon class
   * @returns Css icon class
   */
  public getTabIconClassByCategory(keySegment): string {
    return this._environmentService.getIconCategory(Number(keySegment));
  }

  /**
   * Opens the actions menu (popover anchored to the ⋮ button)
   * @param event click on the ⋮ button
   */
  public async onClickOpenSheetEnvironment(event: Event): Promise<void> {
    if (this._isLoadingActionSheet) {
      return;
    }
    this._isLoadingActionSheet = true;
    try {
      let idEnvironment = this.currentEnvironment.id;

      const result = await this._actionSheetService.openMenuActionEnvironment(
        event,
        this.currentEnvironment.category,
        this.generalSettings,
        this.isDemoMode,
        this._envHasDevicesAndChildren,
        this.currentEnvironment.removable
      );
      switch (result.data?.action) {
        case ActionEnvironments.UPDATE:
          this._routerService.navigateToEnvironmentEdit(
            this.idArea,
            idEnvironment
          );
          break;
        case ActionEnvironments.DELETE:
          await this.deleteEnvironment(this.currentEnvironment);
          break;
        case ActionEnvironments.ASSOCIATE_USER:
          this._routerService.navigateToEnvironmentUsersAssociated(
            this.idArea,
            idEnvironment
          );
          break;
        case ActionEnvironments.MANAGE_DEVICES:
          this._routerService.navigateToEnvironmentManageDevices(
            this.idArea,
            idEnvironment
          );
          break;
        case ActionEnvironments.ACCESS_MANAGEMENT:
          this._routerService.navigateToEnvironmentAccessManagement(
            this.idArea,
            idEnvironment
          );
          break;
        case ActionEnvironments.ADD_PART:
          await this.onClickOpenPopupAddEnvironment(
            idEnvironment,
            this.currentEnvironment.category
          );
          break;
        case ActionEnvironments.IMPORT_READINGS:
          this._routerService.navigateToImportReadings(
            this.idArea,
            idEnvironment
          );
          break;
      }
    } finally {
      this._isLoadingActionSheet = false;
    }
  }

  /**
   * Show and hide environment children elements
   * @param event
   */
  public onClickToggleShowChildren(): void {
    this.showChildren = !this.showChildren;
    this._environmentService.setExpanded(
      this.currentEnvironment.id,
      this.showChildren
    );
  }

  /**
   * Emit delete environment event for children items
   * @param environment: environment obj taken as an event
   */
  public deleteEnvironmentChildren(environment: EnvironmentTree): void {
    this.deleteEnv.emit(environment);
  }

  /**
   * Open grid smart component when clicking the relative button (in supervisions page)
   * @param id Environment id
   */
  public onClickSmartComponent(id: number): void {
    this._systemRouterService.navigateToSupervisionSmartComponent(id);
  }

  /**
   * Open graphic page for smart component when clicking the relative button (in supervisions page)
   * @param id Environment id
   */
  public onClickSupervisionGraphic(id: number): void {
    this._systemRouterService.navigateToGraphicSupervisionSmartComponent(id);
  }

  /**
   * Open popup to create new environment by category
   */
  private async onClickOpenPopupAddEnvironment(
    idParent: number,
    envCategory: EnvironmentCategory
  ): Promise<void> {
    const category = await this._environmentService.openPopupCategory(
      envCategory
    );

    if (category.data && category.data > 0) {
      this._environmentService.editEnvironment.next({
        category: category.data,
        idParent,
      });
      this._routerService.navigateToAddEnvironment(this.idArea);
    }
  }

  /**
   * Load current user data
   */
  private loadCurrentUserData(): void {
    this._selectCurrentUserSub = this._store
      .select(currentUserData)
      .pipe(
        filter((currentUserData: GenericUserData) => currentUserData !== null),
        map((currentUserData: GenericUserData) => currentUserData?.currentUser)
      )
      .subscribe(async (currentUser: CurrentSessionData) => {
        this.currentUser = currentUser;
      });
  }

  /**
   * Return choosen environment.
   */
  public onClickConfirm() {
    if (this.environmentSelected) {
      this._modalCtrl.dismiss({
        id: this.environmentSelected.id,
        name: (this.environmentSelected as Environment).nRoom,
      });
    } else {
      this._modalCtrl.dismiss();
    }
  }

  //#region Trnslation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.noEnvironmentFound",
        "dictionary.searchEnvironmentAccess",
        "dictionary.gateway",
        "dictionary.close",
        "dictionary.nRoom",
        "dictionary.description",
        "dictionary.delete",
        "dictionary.manage",
        "dictionary.otherUser",
        "dictionary.otherUserNext",
        "dictionary.confirmDeleteBuildingPart",
        "dictionary.deleteMonitoringPart",
      ])
      .subscribe((translation) => {
        this.noEnviromentFound = translation["dictionary.noEnvironmentFound"];
        this.searchEnv = translation["dictionary.searchEnvironmentAccess"];
        this.gatewayName = translation["dictionary.gateway"];
        this.nRoom = translation["dictionary.nRoom"];
        this.description = translation["dictionary.description"];
        this._lblClose = translation["dictionary.close"];
        this._lblDelete = translation["dictionary.delete"];
        this._lblManage = translation["dictionary.manage"];
        this._lblOther = translation["dictionary.otherUser"];
        this._lblUser = translation["dictionary.otherUserNext"];
        this._lblConfirmDeleteBuildingPart =
          translation["dictionary.confirmDeleteBuildingPart"];
        this._lblDeleteMonitoringPart =
          translation["dictionary.deleteMonitoringPart"];
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    this._environmentStoreSubscription?.unsubscribe();
  }
  //#endregion

  /**
   * Try to emit current environment value for deleteEnv event
   * @param environment environment obj of current selected environment
   */
  private async deleteEnvironment(environment: EnvironmentTree): Promise<void> {
    try {
      this.deleteEnv.emit(environment);
    } catch {
      this._alertService.presentAlertSomethingWrong();
    }
  }

  /**
   * Set envHasDevicesAndChildren flag to true if env has associated devices and children
   */
  private checkIfEnvironmentHasDevicesAndChildren(): void {
    if (
      this.currentEnvironment.hasDevices ||
      this.currentEnvironment?.children?.length > 0
    ) {
      this._envHasDevicesAndChildren = true;
    }
    if (
      this._themeService.applicationTheme == ApplicationTheme.DIGITAL_PLACE ||
      this._themeService.applicationTheme == ApplicationTheme.DREX
    ) {
      this.showGrid = false;
    }
  }

  /**
   * Subscribe network state
   */
  private subsribeNetworkServiceData(): void {
    this._networkSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );

          if (this.environmentFilteredList != null) {
            this.environmentFilteredList = [...this.environmentFilteredList];
          }
        }
      );
  }

  /**
 Estabilish subscription to "serverSettings" Store feature (which stores the info about visibility permission on the menù subsections:
 * each item of the menu is visible only if the user has the permission)
   */
  private loadSettingsData(): void {
    this._selectserverSettingsSub = this._store
      .select(serverSettings)
      .pipe(filter((serverSetting) => serverSetting !== null))
      .subscribe((serverSetting: Settings) => {
        this.generalSettings = serverSetting;
        if (
          this.generalSettings.AllowChangesBuildingPart ||
          this.generalSettings.ShowDeviceListPartsBuilding
        ) {
          this.showActionMenu = true;
        }
      });
  }
}
